import { Button, Link, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { DateTimePicker } from "@mui/x-date-pickers";
import { Dayjs } from "dayjs";
import { type Community } from "../common/api/communityApi";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { communityApi } from "../common/api";
import { useAsyncData } from "../common/networking";

import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import { ExportCard } from "./ExportCard";

export function OtlExport() {
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [community, setCommunity] = useState<Community | "All">("All");

  const { data: communities } = useAsyncData(
    () => communityApi.getCommunities(),
    [],
  );

  const invalidRange = Boolean(
    (fromDate && !fromDate.isValid()) ||
      (toDate && !toDate.isValid()) ||
      (fromDate && toDate && toDate.isBefore(fromDate, "minute")),
  );
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (fromDate?.isValid()) {
      params.append("fromDate", fromDate.format("YYYY-MM-DDTHH:mmZ"));
    }
    if (toDate?.isValid()) {
      params.append("toDate", toDate.format("YYYY-MM-DDTHH:mmZ"));
    }
    if (community) {
      params.append("community", community);
    }
    return params;
  }, [community, fromDate, toDate]);

  return (
    <ExportCard
      id="otl-export-title"
      title="On the Land (OTL)"
      description="Export applications or beneficiaries using the same date and time range and community filter."
      icon={<DescriptionOutlined />}
      actions={
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <Button
            fullWidth
            disabled={invalidRange}
            variant="contained"
            component={Link}
            href={`${process.env.NODE_ENV === "development" ? "http://localhost:8101" : ""}/internal/otl-application-export?${queryParams.toString()}`}
            download
          >
            Export OTL applications
          </Button>
          <Button
            fullWidth
            disabled={invalidRange}
            variant="outlined"
            component={Link}
            href={`${process.env.NODE_ENV === "development" ? "http://localhost:8101" : ""}/internal/otl-beneficiary-export?${queryParams.toString()}`}
            download
          >
            Export OTL beneficiaries
          </Button>
        </Stack>
      }
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <DateTimePicker
          label="From date and time"
          format="DD/MM/YYYY HH:mm"
          ampm={false}
          timeSteps={{ minutes: 1 }}
          value={fromDate}
          onChange={setFromDate}
          slotProps={{
            textField: {
              fullWidth: true,
              variant: "outlined",
              InputLabelProps: { shrink: true },
            },
          }}
        />
        <DateTimePicker
          label="To date and time"
          format="DD/MM/YYYY HH:mm"
          ampm={false}
          timeSteps={{ minutes: 1 }}
          value={toDate}
          onChange={setToDate}
          slotProps={{
            textField: {
              fullWidth: true,
              variant: "outlined",
              InputLabelProps: { shrink: true },
            },
          }}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Times are in {timezone}. The end time includes the selected minute.
      </Typography>
      {invalidRange && (
        <Typography role="alert" variant="body2" color="error">
          Enter valid dates and times, with the end at or after the start.
        </Typography>
      )}
      <TextField
        select
        fullWidth
        variant="outlined"
        label="Community"
        value={community}
        onChange={(e) => setCommunity(e.target.value as Community)}
      >
        <MenuItem value="All">All</MenuItem>
        {communities?.map((c) => (
          <MenuItem key={c.id} value={c.id}>
            {c.name}
          </MenuItem>
        ))}
      </TextField>
    </ExportCard>
  );
}
